import debounce from 'lodash/debounce';
import toString from 'lodash/toString';
import uniqBy from 'lodash/uniqBy';
import difference from 'lodash/difference';
import {
  useState, useEffect, useRef,
} from 'react';
import useEventListener from './useEventListener';

export default function useRemote({
  value,
  open,
  defaultOpen,
  onFetchMore,
  remoteSearch,
  filterOption,
  autoLoadSelectedOption,
  autoClearSearchValue = true,
  ...props
}) {
  const ref = useRef({
    page: 1,
    visible: open || defaultOpen,
    search: '',
    options: [],
    loaded: false,
    loadedOptionValue: undefined,
    onSearch: null,
    onFetchMore: null,
    searchId: 0,
  });
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const onSearch = useEventListener(props, 'onSearch', (search) => { ref.current.onSearch(search); });
  const onChange = useEventListener(props, 'onChange', () => {
    if (autoClearSearchValue && ref.current.search) {
      onSearch('');
    }
  });
  const onDropdownVisibleChange = useEventListener(props, 'onDropdownVisibleChange', (visible) => {
    const self = ref.current;

    self.visible = visible;
    // 在下拉菜单打开时加载选项列表
    // 下拉菜单隐藏后会清空搜索词，所以需要在下次打开时重新请求新选项列表
    if (visible && (!self.loaded || self.search)) {
      onSearch();
    }
    if (self.onDropdownVisibleChange) {
      self.onDropdownVisibleChange(visible);
    }
  });
  const onPopupScroll = useEventListener(props, 'onPopupScroll', (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    const loadable = scrollTop + clientHeight >= scrollHeight - clientHeight / 2;
    if (loadable && !ref.current.finished) {
      ref.current.lazyLoad(ref.current.search);
    }
  });

  ref.current.options = options;
  ref.current.onFetchMore = onFetchMore;
  ref.current.remoteSearch = remoteSearch;

  useEffect(() => {
    const self = ref.current;
    self.load = async (search = '', missingOptions) => {
      if (self.search !== search) {
        self.search = search;
        self.page = 1;
        self.finished = false;
      }
      if (self.finished) {
        return;
      }
      self.searchId += 1;
      setLoading(true);
      const { searchId } = self;
      const res = await self.onFetchMore({ search, page: self.page, missingOptions });
      if (searchId !== self.searchId) {
        return;
      }
      setLoading(false);
      if (res) {
        if (self.page === 1) {
          setOptions(res.list);
        } else {
          setOptions((prevOptions) => uniqBy([...prevOptions, ...res.list]));
        }
        if (res.list.length < 1) {
          self.finished = true;
        } else {
          self.finished = self.options.length >= res.total;
        }
        self.page += 1;
      }
      self.loaded = true;
    };
    self.lazyLoad = debounce(self.load, 500);
    self.onSearch = async (search = '', missingOptions) => {
      if (ref.current.visible) {
        // 启用远程搜索时，重新拉取选项列表
        if (self.remoteSearch) {
          self.lazyLoad(search, missingOptions);
        } else {
          // 否则拉取下一页选项
          // FIXME: 下一页选项可能不匹配搜索条件，导致搜索结果少于实际结果
          self.lazyLoad('', missingOptions);
        }
      }
    };
  }, []);

  // 在菜单打开时加载选项列表
  useEffect(() => {
    if (open || defaultOpen) {
      onDropdownVisibleChange(true);
    }
  }, [open, defaultOpen, onDropdownVisibleChange]);

  // 加载已选中项
  useEffect(() => {
    const self = ref.current;
    const val = typeof value === 'undefined' || value === null ? undefined : value;
    const valueList = Array.isArray(val) ? val : [val];
    const missingOptions = difference(valueList, self.options.map((option) => option.value));

    if (
      autoLoadSelectedOption && typeof val !== 'undefined'
      && toString(self.loadedOptionValue) !== toString(val)
      && missingOptions.length > 0
    ) {
      self.load(self.search, missingOptions);
    }
    self.loadedOptionValue = val;
  }, [value, autoLoadSelectedOption]);

  return {
    ...props,
    loading,
    open,
    value,
    defaultOpen,
    onSearch,
    // 启用远程搜索时禁用本地搜索
    filterOption: remoteSearch ? false : filterOption,
    options,
    onChange,
    onDropdownVisibleChange,
    onPopupScroll,
  };
}
